import type {
  GetKeyFn,
  GetParentKeyFn,
  Key,
  NodeValues,
  TreeNode,
  TreeNodesMap,
} from "./types"

export const _ROOT_ = "ROOT"

export function isExpandable(node?: TreeNode): boolean {
  if (!node) return false
  return !!node.children?.length
}

export function isRoot(node?: TreeNode): boolean {
  if (!node) return false
  return node.parentId === _ROOT_
}

export function getNodeDepth(
  node: TreeNode,
  tree: TreeNodesMap,
  initialDepth?: number,
): number {
  if (!node) return 0
  let depth: number = initialDepth ?? -1
  function inc(node: TreeNode) {
    if (node.parentId) {
      depth++
      let parent = tree[node.parentId]
      if (parent) {
        inc(parent)
      }
    }
  }
  inc(node)
  return depth
}

export function getSiblingNodes(node: TreeNode, tree: TreeNodesMap): Key[] {
  if (!node) return []
  if (!node.parentId) return []
  let parentNode = tree[node.parentId!]
  if (!parentNode) return []
  return parentNode.children || []
}

export function allChildren(nodeId: Key, tree: TreeNodesMap): Key[] {
  const res: Key[] = []
  const node = tree[nodeId]
  if (!node) return []
  const s = [nodeId]
  while (s.length) {
    const n = s.shift()
    if (n && tree[n]) {
      let cs = tree[n].children
      if (cs) {
        res.push(...cs)
        s.push(...cs)
      }
    }
  }

  return res
}

export function buildTreeNodesMap<T>(
  items: NodeValues<T>[],
  getKey: GetKeyFn<T>,
  getParentKey: GetParentKeyFn<T>,
  compare?: CompareFunction<Key>,
  rootId?: string,
  initialDepth?: number,
): TreeNodesMap {
  let map: TreeNodesMap = {
    [rootId || _ROOT_]: {
      id: rootId || _ROOT_,
      depth: -1,
      children: null,
    },
  }

  items.forEach((it) => {
    const key = getKey(it)
    assertKey(key)
    const node: TreeNode = {
      id: key,
      parentId: getParentKey(it) ?? _ROOT_,
      children: null,
      depth: 0,
    }
    map[node.id] = node
  })

  for (let [key, node] of Object.entries(map)) {
    //#region parents
    if (node.parentId) {
      let parent = map[node.parentId]
      if (parent) {
        let children: Key[] = parent.children || []
        if (compare) {
          pushAtSortPosition(children, key, compare, true)
        } else {
          children.push(key)
        }
        parent.children = children
        map[node.parentId!] = parent
      }
    }
    //#endregion

    // depth
    node.depth = getNodeDepth(node, map, initialDepth)

    map[node.id] = node
  }

  return map
}

export function assertKey(key: any) {
  if (!key || typeof key !== "string") {
    throw new TypeError("getKey should return a string")
  }
}

/**
 * copied from npm 'array-push-at-sort-position'
 * @link https://github.com/pubkey/array-push-at-sort-position/blob/master/src/index.js
 */
export type CompareFunction<T> = (a: T, b: T) => 1 | 0 | -1
export function pushAtSortPosition<T>(
  array: T[],
  item: T,
  compareFunction: CompareFunction<T>,
  mutate?: boolean,
) {
  const ret = mutate ? array : array.slice()

  let high = ret.length - 1
  let low = 0
  let mid = 0

  if (ret.length === 0) {
    ret.push(item)
    return ret
  }

  while (low <= high) {
    mid = low + ((high - low) >> 1)
    const _cmp = compareFunction(ret[mid], item)
    if (_cmp <= 0.0) {
      // searching too low
      low = mid + 1
    } else {
      // searching too high
      high = mid - 1
    }
  }

  const cmp = compareFunction(ret[mid], item)
  if (cmp <= 0.0) {
    mid++
  }

  ret.splice(mid, 0, item)
  return ret
}

export function insertAtIndexInline<T>(xs: T[], at: number, ys: T[]) {
  xs.splice(at, 0, ...ys)
}

/**
 * copied from npm 'array-move'
 * @link https://github.com/sindresorhus/array-move/blob/main/index.js
 */
export function arrayMoveMutate<T>(array: T[], from: number, to: number) {
  const startIndex = from < 0 ? array.length + from : from

  if (startIndex >= 0 && startIndex < array.length) {
    const endIndex = to < 0 ? array.length + to : to

    const [item] = array.splice(from, 1)
    array.splice(endIndex, 0, item)
  }
}

export function arrayMove<T>(array: T[], from: number, to: number) {
  array = [...array]
  arrayMoveMutate(array, from, to)
  return array
}
